import React, { useEffect, useState } from "react";
import { TrendingUp, Plus } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Page, Card, Pill, PrimaryButton, EmptyState } from "../components/UI";
import api from "../lib/api";
import { toast } from "sonner";

export default function Outcomes() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [outcomes, setOutcomes] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ client_id: "", measure_type: "PHQ-9", score: 0 });

  useEffect(() => {
    api.get("/clients").then(({ data }) => { setClients(data); if (data.length) setSelectedClient(data[0].id); });
  }, []);

  useEffect(() => {
    if (selectedClient) {
      api.get(`/outcomes?client_id=${selectedClient}`).then(({ data }) => setOutcomes(data));
    }
  }, [selectedClient]);

  const record = async (e) => {
    e.preventDefault();
    try {
      await api.post("/outcomes", { ...form, score: parseInt(form.score) });
      toast.success("Recorded");
      setModal(false);
      if (form.client_id === selectedClient) {
        const { data } = await api.get(`/outcomes?client_id=${selectedClient}`);
        setOutcomes(data);
      }
    } catch { toast.error("Failed"); }
  };

  const grouped = outcomes.reduce((acc, o) => {
    if (!acc[o.measure_type]) acc[o.measure_type] = [];
    acc[o.measure_type].push({ date: new Date(o.date).toLocaleDateString([], { month: "short", day: "numeric" }), score: o.score });
    return acc;
  }, {});

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <Page
      title="Outcomes & Reporting"
      subtitle="Trend graphs, recurring measures, funder-ready exports."
      actions={<PrimaryButton onClick={() => { setForm({ ...form, client_id: selectedClient }); setModal(true); }} testid="record-outcome-btn"><span className="flex items-center gap-2"><Plus className="w-4 h-4" /> Record Measure</span></PrimaryButton>}
    >
      <Card delay={0.05} className="mb-5">
        <select value={selectedClient} onChange={(e) => setSelectedClient(e.target.value)} className="w-full md:w-80 px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="outcome-client-select">
          <option value="">Select client…</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
        </select>
      </Card>

      {Object.keys(grouped).length === 0 ? <EmptyState icon={TrendingUp} title="No outcome measures yet" description="Track PHQ-9, GAD-7, PCL-5 scores over time." /> : (
        <div className="grid md:grid-cols-2 gap-5">
          {Object.entries(grouped).map(([measure, data], i) => (
            <Card key={measure} delay={0.1 + i*0.05} testid={`outcome-chart-${measure}`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-serif text-xl">{measure}</h3>
                  <div className="text-xs" style={{ color: "var(--mf-text-muted)" }}>{data.length} measure{data.length !== 1 && "s"}</div>
                </div>
                <Pill variant={data[data.length-1].score < data[0].score ? "success" : "warn"}>
                  {data[data.length-1].score < data[0].score ? "Improving" : "Monitor"}
                </Pill>
              </div>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                    <XAxis dataKey="date" stroke="var(--mf-text-muted)" style={{ fontSize: "12px" }} />
                    <YAxis stroke="var(--mf-text-muted)" style={{ fontSize: "12px" }} />
                    <Tooltip contentStyle={{ background: "var(--mf-surface)", border: "none", borderRadius: "12px" }} />
                    <Line type="monotone" dataKey="score" stroke="var(--mf-primary)" strokeWidth={2.5} dot={{ fill: "var(--mf-primary)", r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>
          ))}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setModal(false)}>
          <div className="mf-card w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-serif text-2xl mb-4">Record Measure</h2>
            <form onSubmit={record} className="space-y-3">
              <select required value={form.client_id} onChange={set("client_id")} className="w-full px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="new-outcome-client">
                <option value="">Select client…</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
              </select>
              <select value={form.measure_type} onChange={set("measure_type")} className="w-full px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="new-outcome-type">
                {["PHQ-9", "GAD-7", "PCL-5", "Columbia", "AUDIT", "MDQ"].map(t => <option key={t}>{t}</option>)}
              </select>
              <input required type="number" placeholder="Score" value={form.score} onChange={set("score")} className="w-full px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="new-outcome-score" />
              <div className="flex items-center justify-end gap-2 pt-2">
                <button type="button" onClick={() => setModal(false)} className="px-4 py-2 text-sm rounded-full">Cancel</button>
                <PrimaryButton type="submit" testid="submit-new-outcome">Record</PrimaryButton>
              </div>
            </form>
          </div>
        </div>
      )}
    </Page>
  );
}
